import { useState, useEffect, useCallback } from 'react'
import { apiClient } from '../api/client'

type UseSettingsResult = {
  configured: boolean
  codmonId: string | null
  loading: boolean
  saving: boolean
  error: string | null
  saveCredentials: (codmonId: string, codmonPassword: string) => Promise<boolean>
  deleteCredentials: () => Promise<boolean>
}

/**
 * 設定（資格情報）を管理するフック
 */
export function useSettings(): UseSettingsResult {
  const [configured, setConfigured] = useState(false)
  const [codmonId, setCodmonId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await apiClient.getCredentialStatus()
        setConfigured(response.configured)
        setCodmonId(response.codmon_id ?? null)
      } catch (err) {
        console.error('Failed to fetch credential status:', err)
        setError('設定の読み込みに失敗しました')
      } finally {
        setLoading(false)
      }
    }

    fetchStatus()
  }, [])

  const saveCredentials = useCallback(async (id: string, password: string): Promise<boolean> => {
    setSaving(true)
    setError(null)

    try {
      const response = await apiClient.saveCredentials({
        codmonId: id,
        codmonPassword: password,
      })
      setConfigured(true)
      setCodmonId(response.codmon_id)
      return true
    } catch (err) {
      console.error('Failed to save credentials:', err)
      setError(err instanceof Error ? err.message : '資格情報の保存に失敗しました')
      return false
    } finally {
      setSaving(false)
    }
  }, [])

  const deleteCredentials = useCallback(async (): Promise<boolean> => {
    setSaving(true)
    setError(null)

    try {
      await apiClient.deleteCredentials()
      setConfigured(false)
      setCodmonId(null)
      return true
    } catch (err) {
      console.error('Failed to delete credentials:', err)
      setError(err instanceof Error ? err.message : '資格情報の削除に失敗しました')
      return false
    } finally {
      setSaving(false)
    }
  }, [])

  return {
    configured,
    codmonId,
    loading,
    saving,
    error,
    saveCredentials,
    deleteCredentials,
  }
}
